import { useState } from 'react';
import {
  HelpCircle,
  Wallet,
  PlusCircle,
  Settings,
  Calendar,
  Target,
  Image,
  CheckCircle2,
  Play,
  BarChart3,
  ChevronRight,
  Sparkles,
  Bell,
  Store,
  LayoutGrid,
} from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/molecules/sheet';
import { Button } from '@/components/atoms/button';

interface AdsHelpDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface HelpStep {
  number: number;
  title: string;
  description: string;
  icon: typeof Wallet;
  tips?: string[];
}

const HELP_STEPS: HelpStep[] = [
  {
    number: 1,
    title: 'Rechargez votre portefeuille',
    description:
      "Avant de créer une publicité, vous devez avoir de l'argent dans votre portefeuille publicitaire.",
    icon: Wallet,
    tips: [
      'Allez dans "Portefeuille publicitaire"',
      'Cliquez sur "Recharger"',
      'Choisissez un montant et payez',
    ],
  },
  {
    number: 2,
    title: 'Créez une nouvelle campagne',
    description:
      'Une campagne est votre publicité. Donnez-lui un nom pour la reconnaître facilement.',
    icon: PlusCircle,
    tips: [
      'Cliquez sur "Nouvelle campagne"',
      'Choisissez un nom simple (ex: "Promo été 2024")',
    ],
  },
  {
    number: 3,
    title: 'Choisissez le type de publicité',
    description:
      "Il existe plusieurs façons de promouvoir vos produits. Chaque type a un prix et un objectif différent.",
    icon: Settings,
  },
  {
    number: 4,
    title: 'Définissez votre budget',
    description:
      "C'est le montant maximum que vous voulez dépenser. La publicité s'arrête quand le budget est épuisé.",
    icon: Target,
    tips: [
      'Commencez petit pour tester',
      'Vous pouvez augmenter le budget plus tard',
    ],
  },
  {
    number: 5,
    title: 'Choisissez les dates',
    description:
      'Sélectionnez quand votre publicité doit commencer et se terminer.',
    icon: Calendar,
    tips: [
      'Vous pouvez lancer une pub immédiatement',
      'Planifiez à l\'avance pour les événements (fêtes, soldes...)',
    ],
  },
  {
    number: 6,
    title: 'Sélectionnez vos produits ou créez votre visuel',
    description:
      'Selon le type de pub, ajoutez les produits à promouvoir ou créez une bannière.',
    icon: Image,
    tips: [
      'Choisissez vos meilleurs produits',
      'Les images de qualité attirent plus de clients',
    ],
  },
  {
    number: 7,
    title: 'Vérifiez et lancez !',
    description:
      'Relisez les informations et cliquez sur "Créer" pour lancer votre publicité.',
    icon: CheckCircle2,
  },
];

const AD_TYPES_INFO = [
  {
    type: 'sponsored_product',
    name: 'Produits sponsorisés',
    description: 'Vos produits apparaissent en haut des résultats de recherche',
    icon: Sparkles,
    price: '0.05€ par clic',
  },
  {
    type: 'homepage_carousel',
    name: 'Carrousel page d\'accueil',
    description: 'Grande bannière visible par tous les visiteurs',
    icon: Image,
    price: '10€ par jour',
  },
  {
    type: 'featured_shop',
    name: 'Boutique mise en avant',
    description: 'Votre boutique est mise en avant sur le site',
    icon: Store,
    price: '15€ par semaine',
  },
  {
    type: 'category_banner',
    name: 'Bannière catégorie',
    description: 'Votre bannière apparaît dans une catégorie de produits',
    icon: LayoutGrid,
    price: '5€ par jour',
  },
  {
    type: 'push_all_users',
    name: 'Notification à tous',
    description: 'Envoyez une notification à tous les utilisateurs',
    icon: Bell,
    price: '50€ par envoi',
  },
];

export function AdsHelpDrawer({ open, onOpenChange }: AdsHelpDrawerProps) {
  const [activeSection, setActiveSection] = useState<'steps' | 'types'>('steps');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="flex items-center gap-2">
            <HelpCircle className="size-5 text-primary" />
            Aide - Publicités
          </SheetTitle>
          <SheetDescription>
            Apprenez à créer et gérer vos publicités
          </SheetDescription>
        </SheetHeader>

        {/* Section Tabs */}
        <div className="flex gap-2 p-4 border-b">
          <Button
            variant={activeSection === 'steps' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection('steps')}
          >
            <Play className="size-4 mr-1.5" />
            Comment créer une pub
          </Button>
          <Button
            variant={activeSection === 'types' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection('types')}
          >
            <BarChart3 className="size-4 mr-1.5" />
            Types de pubs
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {activeSection === 'steps' ? (
            <>
              <p className="text-sm text-muted-foreground">
                Suivez ces étapes pour créer votre première publicité :
              </p>
              <div className="space-y-4">
                {HELP_STEPS.map((step) => (
                  <div
                    key={step.number}
                    className="relative pl-12 pb-4 last:pb-0"
                  >
                    {/* Vertical line */}
                    {step.number < HELP_STEPS.length && (
                      <div className="absolute left-[18px] top-10 bottom-0 w-0.5 bg-border" />
                    )}

                    {/* Step number circle */}
                    <div className="absolute left-0 top-0 size-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <step.icon className="size-4 text-primary" />
                    </div>

                    {/* Content */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                          Étape {step.number}
                        </span>
                      </div>
                      <h4 className="font-medium text-sm">{step.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {step.description}
                      </p>
                      {step.tips && (
                        <ul className="mt-2 space-y-1">
                          {step.tips.map((tip, index) => (
                            <li
                              key={index}
                              className="flex items-start gap-2 text-xs text-muted-foreground"
                            >
                              <ChevronRight className="size-3 mt-0.5 text-primary shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="pt-4 border-t">
                <Button
                  className="w-full"
                  onClick={() => {
                    onOpenChange(false);
                    window.location.href = '/ads/campaigns/new';
                  }}
                >
                  <PlusCircle className="size-4 mr-2" />
                  Créer ma première publicité
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Voici les différents types de publicités disponibles :
              </p>
              <div className="space-y-3">
                {AD_TYPES_INFO.map((adType) => (
                  <div
                    key={adType.type}
                    className="p-3 rounded-lg border bg-card hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <adType.icon className="size-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">{adType.name}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {adType.description}
                        </p>
                        <p className="text-xs font-medium text-primary mt-1.5">
                          {adType.price}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Glossary */}
              <div className="pt-4 border-t">
                <h4 className="font-medium text-sm mb-3">Lexique</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2">
                    <span className="font-medium text-primary min-w-24">Impressions</span>
                    <span className="text-muted-foreground">
                      Nombre de fois que votre pub a été vue
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-primary min-w-24">Clics</span>
                    <span className="text-muted-foreground">
                      Nombre de fois qu'on a cliqué sur votre pub
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-primary min-w-24">CTR</span>
                    <span className="text-muted-foreground">
                      Pourcentage de personnes qui cliquent après avoir vu
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <span className="font-medium text-primary min-w-24">Budget</span>
                    <span className="text-muted-foreground">
                      Montant maximum à dépenser pour la pub
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Help Button component to use in pages
export function AdsHelpButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-1.5"
      >
        <HelpCircle className="size-4" />
        <span className="hidden sm:inline">Aide</span>
      </Button>
      <AdsHelpDrawer open={open} onOpenChange={setOpen} />
    </>
  );
}
